if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initNotifyCtaObserver();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.notifyCtaObserver?.disconnect();
        if (this.notifyCtaFrame) cancelAnimationFrame(this.notifyCtaFrame);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      initNotifyCtaObserver() {
        if (!this.querySelector('.hass-product__cta-panel')) return;

        this.syncNotifyCtas();
        this.notifyCtaObserver?.disconnect();
        this.notifyCtaObserver = new MutationObserver(() => this.scheduleNotifyCtaSync());
        this.notifyCtaObserver.observe(this, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'disabled'],
        });
      }

      scheduleNotifyCtaSync() {
        if (this.notifyCtaFrame) return;

        this.notifyCtaFrame = requestAnimationFrame(() => {
          this.notifyCtaFrame = null;
          this.syncNotifyCtas();
        });
      }

      syncNotifyCtas() {
        this.querySelectorAll('.hass-product__cta-panel').forEach((panel) => {
          const notifyTriggers = this.getNotifyTriggers(panel);
          const hasVisibleNotifyTrigger = notifyTriggers.some((trigger) => this.isVisibleNotifyTrigger(trigger));
          const submitButton = panel.querySelector('.product-form__submit');
          const submitButtonText = submitButton?.innerText?.trim().toLowerCase() || '';
          const soldOutLabels = [window.variantStrings?.soldOut, window.variantStrings?.unavailable]
            .filter(Boolean)
            .map((label) => label.trim().toLowerCase());
          const isSoldOutState =
            !!submitButton &&
            (submitButton.hasAttribute('disabled') || soldOutLabels.some((label) => submitButtonText === label));

          panel.classList.toggle('hass-product__cta-panel--has-notify', hasVisibleNotifyTrigger);
          submitButton?.classList.toggle('product-form__submit--sold-out', isSoldOutState);
        });
      }

      getNotifyTriggers(panel) {
        const selector = [
          "button[class*='notify']",
          "button[class*='Notify']",
          "button[class*='back-in-stock']",
          "button[class*='BackInStock']",
          "button[class*='klaviyo-bis']",
          "button[class*='BIS_trigger']",
          "button[class*='gw-button-widget']",
          '[data-bis-trigger]',
          '[data-notify-trigger]',
          "[class*='gw-button-widget']",
        ].join(', ');
        const clickableSelector = "button, a, [role='button'], input[type='button'], input[type='submit']";
        const triggers = new Set();

        panel.querySelectorAll(selector).forEach((candidate) => {
          const trigger = candidate.matches(clickableSelector)
            ? candidate
            : candidate.querySelector(clickableSelector) || candidate;

          triggers.add(trigger);
        });

        panel.querySelectorAll('.hass-product__notify-trigger').forEach((element) => {
          if (!triggers.has(element)) element.classList.remove('hass-product__notify-trigger');
        });

        triggers.forEach((element) => element.classList.add('hass-product__notify-trigger'));

        return [...triggers];
      }

      isVisibleNotifyTrigger(element) {
        if (!element?.isConnected || element.hidden || element.getAttribute('aria-hidden') === 'true') return false;

        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return false;

        return element.getClientRects().length > 0;
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const locallySelectedVariant = this.getVariantFromSelectedOptions();

        this.updateProductInfoFromLocalVariant(locallySelectedVariant, productUrl);

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        this.toggleSubmitButton(true);
        this.clearProductFormError();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateProductInfoFromLocalVariant(variant, productUrl) {
        if (!this.variantStateData) return;

        if (!variant) {
          this.updateURL(productUrl);
          this.updateVariantInputs();
          this.setUnavailable();
          return;
        }

        this.updateURL(productUrl, variant.id);
        this.updateVariantInputs(variant.id);
        this.updatePriceFromLocalVariant(variant);
        this.updateInventoryFromLocalVariant(variant);
        this.updatePickupAvailabilityFromLocalVariant(variant);
        this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
        this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
        this.querySelector(`#Volume-${this.dataset.section}`)?.classList.remove('hidden');
        this.toggleSubmitButton(!variant.available, variant.available ? undefined : window.variantStrings.soldOut);

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: null,
            variant,
          },
        });
      }

      updatePriceFromLocalVariant(variant) {
        const priceContainer = this.querySelector(`#price-${this.dataset.section}`);
        const price = priceContainer?.querySelector('.price');
        if (!priceContainer || !price) return;

        const isOnSale = Number(variant.compare_at_price) > Number(variant.price);
        const priceValue = variant.price_formatted || '';
        const compareAtPriceValue = variant.compare_at_price_formatted || '';

        priceContainer.classList.remove('hidden');
        price.classList.toggle('price--on-sale', isOnSale);
        price.classList.toggle('price--sold-out', !variant.available);

        price.querySelectorAll('.price__regular .price-item--regular').forEach((node) => {
          node.textContent = priceValue;
        });

        const salePrice = price.querySelector('.price__sale .price-item--sale.price-item--last');
        if (salePrice) salePrice.textContent = priceValue;

        const saleCompareAtPrice = price.querySelector('.price__sale .price-item--regular');
        if (saleCompareAtPrice) saleCompareAtPrice.textContent = compareAtPriceValue;

        let savingsBadge = priceContainer.querySelector('.hass-product__price-badge');
        if (isOnSale && variant.savings_formatted) {
          if (!savingsBadge) {
            savingsBadge = document.createElement('span');
            savingsBadge.className = 'badge hass-product__price-badge';
            priceContainer.appendChild(savingsBadge);
          }
          savingsBadge.textContent = `Save ${variant.savings_formatted}`;
        } else {
          savingsBadge?.remove();
        }
      }

      updateInventoryFromLocalVariant(variant) {
        const inventory = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (!inventory) return;

        inventory.classList.remove('hidden');

        if (variant.inventory_management !== 'shopify') {
          inventory.classList.add('visibility-hidden');
          return;
        }

        inventory.classList.remove('visibility-hidden');

        const iconMarkup = inventory.querySelector('.svg-wrapper')?.innerHTML || '';
        const inventoryConfig = this.inventoryConfig;
        if (!inventoryConfig?.messages) return;

        let message = '';
        let color = 'rgb(200, 200, 200)';

        if (variant.inventory_quantity > 0) {
          if (variant.inventory_quantity <= inventoryConfig.threshold) {
            color = 'rgb(238, 148, 65)';
            message = inventoryConfig.showQuantity
              ? inventoryConfig.messages.lowStockWithCount.replace('[quantity]', variant.inventory_quantity)
              : inventoryConfig.messages.lowStock;
          } else {
            color = 'rgb(62, 214, 96)';
            message = inventoryConfig.showQuantity
              ? inventoryConfig.messages.inStockWithCount.replace('[quantity]', variant.inventory_quantity)
              : inventoryConfig.messages.inStock;
          }
        } else if (variant.inventory_policy === 'continue') {
          color = 'rgb(62, 214, 96)';
          message = inventoryConfig.messages.continueSelling;
        } else {
          message = inventoryConfig.messages.outOfStock;
        }

        inventory.innerHTML = iconMarkup
          ? `<span class="svg-wrapper" style="color: ${color}">${iconMarkup}</span>${message}`
          : message;
      }

      updatePickupAvailabilityFromLocalVariant(variant) {
        if (!this.pickupAvailability) return;

        this.pickupAvailability.dataset.variantId = variant.id;
        this.pickupAvailability.update(variant);
      }

      getVariantFromSelectedOptions() {
        if (!this.variantStateData?.variants?.length) return null;

        const selectedOptions = this.selectedOptionNames;
        if (!selectedOptions.length) return null;

        return (
          this.variantStateData.variants.find((variant) => {
            const variantOptions = [variant.option1, variant.option2, variant.option3].filter(
              (value) => value !== null && value !== ''
            );

            if (variantOptions.length !== selectedOptions.length) return false;

            return variantOptions.every((value, index) => value === selectedOptions[index]);
          }) || null
        );
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      clearProductFormError() {
        const productForm = this.productForm;
        if (typeof productForm?.handleErrorMessage === 'function') {
          productForm.handleErrorMessage();
          return;
        }

        const errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
        if (errorMessageWrapper) errorMessageWrapper.hidden = true;
      }

      toggleSubmitButton(disable = true, text) {
        const productForm = this.productForm;
        if (typeof productForm?.toggleSubmitButton === 'function') {
          productForm.toggleSubmitButton(disable, text);
          return;
        }

        const submitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        const submitButtonText = submitButton?.querySelector('span');
        const soldOutMessage = submitButton?.querySelector('.sold-out-message');

        if (!submitButton || !submitButtonText) return;

        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
          if (text) submitButtonText.textContent = text;
        } else {
          submitButton.removeAttribute('disabled');
          submitButtonText.textContent = window.variantStrings.addToCart;
          soldOutMessage?.classList.add('hidden');
          submitButtonText.classList.remove('hidden');
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get inventoryConfig() {
        if (this._inventoryConfig !== undefined) return this._inventoryConfig;

        const configNode = this.querySelector('[data-inventory-config]');
        if (!configNode) {
          this._inventoryConfig = null;
          return this._inventoryConfig;
        }

        try {
          this._inventoryConfig = JSON.parse(configNode.textContent);
        } catch (error) {
          console.error('Unable to parse local inventory config', error);
          this._inventoryConfig = null;
        }

        return this._inventoryConfig;
      }

      get selectedOptionNames() {
        if (!this.variantSelectors) return [];

        return Array.from(this.variantSelectors.querySelectorAll('.product-form__input'))
          .map((inputGroup) => {
            const select = inputGroup.querySelector('select');
            if (select) return select.value;

            return inputGroup.querySelector('input:checked')?.value;
          })
          .filter((value) => value !== undefined);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get variantStateData() {
        if (this._variantStateData !== undefined) return this._variantStateData;

        const dataNode = this.querySelector('[data-variant-state]');
        if (!dataNode) {
          this._variantStateData = null;
          return this._variantStateData;
        }

        try {
          this._variantStateData = JSON.parse(dataNode.textContent);
        } catch (error) {
          console.error('Unable to parse local variant state data', error);
          this._variantStateData = null;
        }

        return this._variantStateData;
      }
    }
  );
}
